import { useEffect, useRef, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import './AutoLogoutModal.css';

const IDLE_TIMEOUT_MS    = 60 * 60 * 1000;       // 1 hour
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;   // 8 hours
const SESSION_START_KEY  = 'sessionStartTime';
const ACTIVITY_EVENTS    = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export default function AutoLogoutModal() {
  const { instance, accounts } = useMsal();
  const [timedOut, setTimedOut] = useState(false);
  const [reason, setReason] = useState('');

  const idleTimerRef    = useRef(null);
  const sessionTimerRef = useRef(null);
  const lastResetRef    = useRef(0);

  const triggerLogout = useCallback((logoutReason) => {
    setReason(logoutReason);
    setTimedOut(true);
  }, []);

  const resetIdleTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < 1000) return; // throttle to once per second
    lastResetRef.current = now;

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => triggerLogout('idle'), IDLE_TIMEOUT_MS);
  }, [triggerLogout]);

  useEffect(() => {
    if (!accounts.length) {
      // User logged out — cancel any running session timer
      if (sessionTimerRef.current) {
        clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
      return;
    }

    // --- Session start tracking ---
    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }

    // --- Idle timeout ---
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    // --- Session timeout (created once per login — not re-created on re-renders) ---
    if (!sessionTimerRef.current) {
      const sessionStart = parseInt(sessionStorage.getItem(SESSION_START_KEY), 10);
      const elapsed      = Date.now() - sessionStart;
      const remaining    = SESSION_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        triggerLogout('session');
      } else {
        sessionTimerRef.current = setTimeout(() => {
          sessionTimerRef.current = null;
          triggerLogout('session');
        }, remaining);
      }
    }

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // Session timer intentionally not cleared here — it must survive re-renders
    };
  }, [accounts, resetIdleTimer, triggerLogout]);

  const handleSignIn = async () => {
    sessionStorage.removeItem(SESSION_START_KEY);
    const account = instance.getActiveAccount() ?? accounts[0];
    try {
      if (account) await instance.clearCache({ account });
    } finally {
      window.location.replace('/');
    }
  };

  if (!timedOut) return null;

  const isIdle = reason === 'idle';

  return (
    <div className="auto-logout-overlay">
      <div className="auto-logout-card">
        <div className="auto-logout-icon">⏱️</div>
        <h2>Session Timed Out</h2>
        <p>
          {isIdle
            ? 'Your session has timed out due to inactivity.'
            : 'Your session has expired after 8 hours.'}
          {' '}Please sign in again to continue.
        </p>
        <button className="btn-primary" onClick={handleSignIn}>
          Sign In Again
        </button>
      </div>
    </div>
  );
}
